import { useCallback, useMemo, useState } from "react";

import type { TagGroup } from "./types";

type KeyboardDirection = "up" | "down" | "enter" | "right" | "left";

export const useKeyboardNavigation = (
  groupedTags: TagGroup,
  keepExpanded: boolean,
  handleSelection: (value: string) => void,
): {
  activeSection: {
    section: string | null;
    index: number | null;
  };
  setActiveSection: (section: { section: string | null; index: number | null }) => void;
  navigateSection: (direction: KeyboardDirection) => void;
} => {
  const sections = useMemo(() => Object.keys(groupedTags), [groupedTags]);

  const [activeSection, setActiveSection] = useState<{
    section: string | null;
    index: number | null;
  }>({
    section: null,
    index: null,
  });

  const getNextSection = useCallback(
    (currentSection: string | null, offset: number): string =>
      findNextSection(groupedTags, sections, currentSection, offset),
    [sections, groupedTags],
  );

  const updateActiveSection = useCallback((section: string | null, index: number | null): void => {
    setActiveSection({ section, index });
  }, []);

  const navigateSection = useCallback(
    (direction: KeyboardDirection) => {
      if (direction === "enter" || direction === "right") {
        if (activeSection.index === null) {
          updateActiveSection(activeSection.section, 0);
        } else if (activeSection.section && direction === "enter") {
          const currentTag = groupedTags[activeSection.section]?.groupTags[activeSection.index];
          if (currentTag) {
            handleSelection(
              currentTag.category_key
                ? `${currentTag.category_key}.${currentTag.field_key}`
                : currentTag.field_key,
            );
            setActiveSection({ section: null, index: null });
          }
        }
        return;
      }

      if (!activeSection.section) {
        const firstAvailableSection = sections.find(
          (x) => (groupedTags[x]?.groupTags.length ?? 0) > 0,
        );
        if (firstAvailableSection)
          updateActiveSection(firstAvailableSection, keepExpanded ? 0 : null);
        return;
      }

      const currentSectionTags = groupedTags[activeSection.section]?.groupTags || [];
      const isFirstItem = activeSection.index === 0;
      const isLastItem = activeSection.index === currentSectionTags.length - 1;

      let newSection: string | null = activeSection.section;
      let newIndex: number | null = activeSection.index;

      if (direction === "up") {
        if (isFirstItem || activeSection.index === null) {
          newSection = getNextSection(activeSection.section, -1);
          newIndex =
            keepExpanded && (groupedTags[newSection]?.groupTags.length ?? 0) > 0
              ? (groupedTags[newSection]?.groupTags.length ?? 0) - 1
              : null;
        } else {
          newIndex = Math.max(activeSection.index - 1, 0);
        }
      }

      if (direction === "down") {
        if (isLastItem || activeSection.index === null) {
          newSection = getNextSection(activeSection.section, 1);
          newIndex =
            keepExpanded && (groupedTags[newSection]?.groupTags.length ?? 0) > 0 ? 0 : null;
        } else {
          newIndex = Math.min(activeSection.index + 1, currentSectionTags.length - 1);
        }
      }

      if (direction === "left" && activeSection.section !== null) {
        newIndex = null;
      }

      updateActiveSection(newSection, newIndex);
    },
    [
      activeSection,
      groupedTags,
      keepExpanded,
      sections,
      getNextSection,
      handleSelection,
      updateActiveSection,
    ],
  );

  return { activeSection, setActiveSection, navigateSection };
};

function findNextSection(
  groupedTags: TagGroup,
  sections: string[],
  currentSection: string | null,
  offset: number,
): string {
  if (sections.every((x) => groupedTags[x]?.groupTags.length === 0)) return sections[0]!;

  const currentIndex = currentSection ? sections.indexOf(currentSection) : -1;
  const newIndex = (currentIndex + offset + sections.length) % sections.length;

  return (groupedTags[sections[newIndex]!]?.groupTags.length ?? 0) > 0
    ? sections[newIndex]!
    : findNextSection(groupedTags, sections, sections[newIndex] ?? null, offset);
}
